<template>
  <div class="qr-code-list">
    <!-- セキュアモード説明 -->
    <v-alert
      v-if="isSecureMode"
      type="info"
      outlined
      class="mb-4"
    >
      <div class="d-flex align-center">
        <v-icon left>
          {{ icons.mdiShieldLock }}
        </v-icon>
        <div>
          <strong>セキュアモード</strong>
          <p class="mb-0 text-caption">
            QRコードは卓ごとに都度発行されます。以下は発行済みのQRコードのみ表示しています。
          </p>
        </div>
      </div>
    </v-alert>

    <!-- ローディング -->
    <div
      v-if="loading"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
      <p class="mt-2">
        読み込み中...
      </p>
    </div>

    <!-- QRコード一覧 -->
    <template v-else>
      <div
        v-for="hall in halls"
        :key="hall.id"
        class="mb-6"
      >
        <div class="d-flex align-center mb-3">
          <h3 class="text-h6 mb-0">
            {{ hall.name }}
          </h3>
          <v-spacer />
          <!-- URLリセットボタン（標準/スタッフモードのみ） -->
          <v-btn
            v-if="!isSecureMode"
            small
            outlined
            color="warning"
            @click="openResetDialog(hall)"
          >
            <v-icon
              small
              left
            >
              {{ icons.mdiRefresh }}
            </v-icon>
            URLリセット
          </v-btn>
        </div>

        <v-alert
          v-if="hall.tables.length === 0"
          type="warning"
          text
          dense
        >
          {{ isSecureMode ? 'QRコードが発行された卓はありません' : '卓がありません' }}
        </v-alert>

        <v-row v-else>
          <v-col
            v-for="table in hall.tables"
            :key="`${hall.id}-${table.tableNumber}`"
            cols="6"
            sm="4"
            md="3"
            lg="2"
            xl="1"
          >
            <v-card outlined>
              <v-card-text class="text-center pa-2">
                <div class="text-subtitle-2 mb-2">
                  {{ table.name || `卓${table.tableNumber}` }}
                </div>

                <!-- QRコード表示 -->
                <div
                  v-if="table.qrUrl"
                  class="qr-wrapper"
                >
                  <canvas :id="`qr-${hall.id}-${table.tableNumber}`" />
                </div>
                <div
                  v-else
                  class="qr-placeholder"
                >
                  <v-icon
                    size="48"
                    color="grey"
                  >
                    {{ icons.mdiQrcode }}
                  </v-icon>
                  <p class="text-caption grey--text mb-0">
                    都度発行
                  </p>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- データなし -->
      <v-alert
        v-if="halls.length === 0"
        type="info"
        text
      >
        ホールが設定されていません
      </v-alert>
    </template>

    <!-- URLリセット確認モーダル -->
    <v-dialog
      v-model="resetDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning--text">
          <v-icon
            left
            color="warning"
          >
            {{ icons.mdiAlert }}
          </v-icon>
          QRコードURLのリセット
        </v-card-title>

        <v-card-text class="pt-4">
          <p class="text-body-1 mb-4">
            <strong>{{ selectedHall && selectedHall.name }}</strong> のQRコードURLをリセットしますか？
          </p>

          <v-alert
            type="error"
            outlined
            dense
            class="mb-0"
          >
            <div class="text-body-2">
              <strong>注意：この操作を行うと、以下の影響があります。</strong>
              <ul class="mt-2 mb-0">
                <li>印刷済みのQRコードは<strong>すべて無効</strong>になります</li>
                <li>お客様に配布済みのURLも使用できなくなります</li>
                <li>新しいQRコードを印刷し直す必要があります</li>
              </ul>
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeResetDialog"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="warning"
            :loading="resetting"
            @click="confirmReset"
          >
            リセットする
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- スナックバー -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      timeout="3000"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from '@vue/composition-api'
import QrCode from 'qrcode'
import {
  mdiShieldLock,
  mdiRefresh,
  mdiQrcode,
  mdiAlert,
} from '@mdi/js'
import SelfOrderSettingApi from '@/api/admin/SelfOrderSetting'

export default {
  name: 'QrCodeList',

  setup() {
    const loading = ref(true)
    const halls = ref([])
    const isSecureMode = ref(false)

    // リセットダイアログ関連
    const resetDialog = ref(false)
    const selectedHall = ref(null)
    const resetting = ref(false)

    // スナックバー
    const showSnackbar = ref(false)
    const snackbarMessage = ref('')
    const snackbarColor = ref('success')

    const generateQrCodes = () => {
      halls.value.forEach(hall => {
        hall.tables.forEach(table => {
          if (table.qrUrl) {
            const canvas = document.getElementById(`qr-${hall.id}-${table.tableNumber}`)
            if (canvas) {
              QrCode.toCanvas(canvas, table.qrUrl, {
                width: 120,
                margin: 1,
              }, error => {
                if (error) console.error('QRコード生成エラー:', error)
              })
            }
          }
        })
      })
    }

    const fetchQrCodes = async () => {
      loading.value = true
      try {
        const response = await SelfOrderSettingApi.getQrCodes()
        halls.value = response.data.halls || []
        isSecureMode.value = response.data.isSecureMode
      } catch (error) {
        console.error('Failed to fetch QR codes:', error)
      } finally {
        loading.value = false
        // loadingがfalseになった後にDOMが更新されるのを待ってからQRコード生成
        await nextTick()
        generateQrCodes()
      }
    }

    const openResetDialog = hall => {
      selectedHall.value = hall
      resetDialog.value = true
    }

    const closeResetDialog = () => {
      resetDialog.value = false
      selectedHall.value = null
    }

    const confirmReset = async () => {
      if (!selectedHall.value) return

      resetting.value = true
      try {
        await SelfOrderSettingApi.resetHallPublicId(selectedHall.value.id)

        snackbarMessage.value = `${selectedHall.value.name} のQRコードURLをリセットしました`
        snackbarColor.value = 'success'
        showSnackbar.value = true

        closeResetDialog()

        // QRコード一覧を再取得
        await fetchQrCodes()
      } catch (error) {
        console.error('Failed to reset hall public ID:', error)
        snackbarMessage.value = 'リセットに失敗しました'
        snackbarColor.value = 'error'
        showSnackbar.value = true
      } finally {
        resetting.value = false
      }
    }

    onMounted(() => {
      fetchQrCodes()
    })

    return {
      loading,
      halls,
      isSecureMode,
      resetDialog,
      selectedHall,
      resetting,
      showSnackbar,
      snackbarMessage,
      snackbarColor,
      openResetDialog,
      closeResetDialog,
      confirmReset,
      icons: {
        mdiShieldLock,
        mdiRefresh,
        mdiQrcode,
        mdiAlert,
      },
    }
  },
}
</script>

<style scoped>
.qr-wrapper {
  display: flex;
  justify-content: center;
  padding: 4px;
  background: white;
  border-radius: 4px;
}

.qr-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  background: #f5f5f5;
  border-radius: 4px;
}
</style>
